import React, { useState, createContext, useMemo, useCallback, useEffect } from 'react';
const valid = value => ['light', 'dark', 'system'].includes(value) ? value : 'system';
const read = key => { try { return localStorage.getItem(key); } catch { return null; } };
const save = (key, value) => { try { localStorage.setItem(key, value); } catch { /* In-memory settings still work. */ } };
export const SettingsContext = createContext({ themeMode: 'light', appearance: 'system', setAppearance: () => {}, compactMode: false });
export default function SettingsProvider({ children }) {
  const [appearance, updateAppearance] = useState(() => valid(read('themeMode')));
  const [systemDark, setSystemDark] = useState(() => Boolean(window.matchMedia?.('(prefers-color-scheme: dark)').matches));
  const [compactMode, setCompactMode] = useState(() => read('compactMode') === 'true');
  const themeMode = appearance === 'system' ? (systemDark ? 'dark' : 'light') : appearance;
  useEffect(() => {
    const media = window.matchMedia?.('(prefers-color-scheme: dark)');
    const update = () => setSystemDark(Boolean(media?.matches));
    update();
    media?.addEventListener?.('change', update);
    return () => media?.removeEventListener?.('change', update);
  }, []);
  useEffect(() => {
    const sync = event => {
      if (event.key === 'themeMode' || event.key === null) updateAppearance(valid(read('themeMode')));
      if (event.key === 'compactMode' || event.key === null) setCompactMode(read('compactMode') === 'true');
    };
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, []);
  useEffect(() => { document.documentElement.style.colorScheme = themeMode; }, [themeMode]);
  const setAppearance = useCallback(value => { const choice = valid(value); updateAppearance(choice); save('themeMode', choice); }, []);
  const toggleThemeMode = useCallback(() => setAppearance(themeMode === 'light' ? 'dark' : 'light'), [themeMode, setAppearance]);
  const toggleCompactMode = useCallback(() => setCompactMode(previous => { save('compactMode', String(!previous)); return !previous; }), []);
  const value = useMemo(() => ({ appearance, setAppearance, themeMode, toggleThemeMode, compactMode, toggleCompactMode }), [appearance, setAppearance, themeMode, toggleThemeMode, compactMode, toggleCompactMode]);
  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}
